<template>
  <v-dialog
    v-model="dialog"
    content-class="v-dialog--custom"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="info"
        dark
        class="mb-2"
        @click="addNew()"
        v-on="on"
        :disabled="!account.data.isAdmin"
      >
        <v-icon>attach_money</v-icon> Add User
      </v-btn>
    </template>
    <v-form ref="form" v-model="valid" @submit.prevent="submit()">
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <alert-message></alert-message>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.userName"
                  label="Username"
                  autocomplete="new-username"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.userName'),
                      serverSideErrors('Username')
                    )
                  "
                  @blur="$v.form.userName.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.email"
                  label="Email"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.email'),
                      serverSideErrors('Email')
                    )
                  "
                  @blur="$v.form.email.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6" v-show="!isEdit">
                <v-text-field
                  v-model="form.password"
                  label="Password"
                  autocomplete="new-password"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.password'),
                      serverSideErrors('Password')
                    )
                  " 
                  @blur="$v.form.password.$touch()"
                  @input="$v.form.password.$touch()"
                  :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPwd ? 'text' : 'password'"
                  @click:append="showPwd = !showPwd"
                />

              </v-col>
              <v-col cols="12" sm="6" md="6" v-show="!isEdit">
                <v-text-field
                  v-model="form.repeatPassword"
                  label="Confirm Password"
                  autocomplete="new-password"
                  :error-messages="fieldErrors('form.repeatPassword')"
                  @blur="$v.form.repeatPassword.$touch()"
                  @input="$v.form.repeatPassword.$touch()"
                  :append-icon="showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPwd ? 'text' : 'password'"
                  @click:append="showPwd = !showPwd"
                />
              </v-col>
            </v-row>

            <v-row v-if="hasAccess('userGroup.edit')">
              <v-col cols="12" sm="12" md="12">
                <user-group-select
                  v-model="form.userGroupId"
                  label="User Group"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.userGroupId'),
                      serverSideErrors('UserGroupId')
                    )
                  "
                  @blur="$v.form.userGroupId.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="form.firstName"
                  label="First Name"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.firstName'),
                      serverSideErrors('FirstName')
                    )
                  "
                  @blur="$v.form.firstName.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="form.lastName"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.lastName'),
                      serverSideErrors('LastName')
                    )
                  "
                  @blur="$v.form.lastName.$touch()"
                  label="Last Name"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.phoneNumber"
                  label="Phone Number"
                  v-mask="'(###) ###-####'"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.phoneNumber'),
                      serverSideErrors('PhoneNumber')
                    )
                  "
                  @blur="$v.form.phoneNumber.$touch()"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- HIRED & FIRED -->

            <v-row v-if="account.data && account.data.user && account.data.user.userGroupName=='Super Admin'">
              <v-col cols="12" sm="6" md="6" lg="6">
                <date-picker
                  v-model="form.hiredDate"
                  label="Hired Date"
                  :error-messages="[].concat(fieldErrors('form.hiredDate'))"
                  @blur="$v.form.hiredDate.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="6" md="6" lg="6" v-show="isEdit">
                <date-picker
                  v-model="form.firedDate"
                  label="Fired Date"
                  :error-messages="[].concat(fieldErrors('form.firedDate'))"
                  @blur="$v.form.firedDate.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="6" lg="6">
                <v-switch
                  v-model="form.isUnion"
                  label="Union"
                  :error-messages="[].concat(fieldErrors('form.isUnion'))"
                  @blur="$v.form.isUnion.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="6" md="6" lg="6">
                <user-select
                  v-model="form.supervisorEmail"
                  label="Supervisor"
                  :error-messages="[].concat(fieldErrors('form.supervisorEmail'))"
                  @blur="$v.form.supervisorEmail.$touch()"
                  @onSelectedObject="onSupervisorSelected"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <h2>Address</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.address"
                  label="Address"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.address'),
                      serverSideErrors('address')
                    )
                  "
                  @blur="$v.form.address.$touch()"
                ></v-text-field>

                <v-text-field
                  v-model="form.address2"
                  label="Address 2"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.address2'),
                      serverSideErrors('address2')
                    )
                  "
                  @blur="$v.form.address2.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="form.city"
                  label="City"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.city'),
                      serverSideErrors('city')
                    )
                  "
                  @blur="$v.form.city.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <state-select
                  v-model="form.state"
                  label="State"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.state'),
                      serverSideErrors('state')
                    )
                  "
                />
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field
                  v-model="form.zipcode"
                  label="Zipcode"
                  :error-messages="
                    [].concat(
                      fieldErrors('form.zipcode'),
                      serverSideErrors('zipcode')
                    )
                  "
                  @blur="$v.form.zipcode.$touch()"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- EMERGENCY -->
            <v-row>
              <v-col>
                <h2>Emergency</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-text-field
                  v-model="form.emergencyContactName"
                  label="Contact Name"
                  :error-messages="
                    [].concat(fieldErrors('form.emergencyContactName'))
                  "
                  @blur="$v.form.emergencyContactName.$touch()"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="12" md="12" lg="6">
                <v-text-field
                  v-model="form.emergencyContactPhone"
                  label="Contact Phone"
                  v-mask="'(###) ###-####'"
                  :error-messages="
                    [].concat(fieldErrors('form.emergencyContactPhone'))
                  "
                  @blur="$v.form.emergencyContactPhone.$touch()"
                />
              </v-col>
              <v-col cols="12" sm="12" md="12" lg="6">
                <v-text-field
                  v-model="form.emergencyContactRelationship"
                  label="Contact Relationship"
                  :error-messages="
                    [].concat(fieldErrors('form.emergencyContactRelationship'))
                  "
                  @blur="$v.form.emergencyContactRelationship.$touch()"
                />
              </v-col>
            </v-row>

            <!-- IS DISABLED -->
            <v-row>
              <v-col cols="12" sm="6" md="6" lg="6">
                <v-switch
                  v-model="form.isActive"
                  label="Active"
                  :error-messages="[].concat(fieldErrors('form.isActive'))"
                  @blur="$v.form.isActive.$touch()"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="darken-1" @click="dialog = false">Cancel</v-btn>
          <v-btn
            :disabled="$v.$invalid"
            type="submit"
            color="info"
            :loading="loading"
          >
            {{ isEdit ? "Save" : "Add" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import UTIL from "src/helpers/utility";
import { mapActions, mapState, mapGetters } from "vuex";
import AlertMessage from "src/components/AlertMessage";
import { isAlphanumeric, isCombinationString } from "src/utils/helpers";
import {
  helpers,
  required,
  email,
  requiredIf,
  sameAs,
  minLength,
} from "vuelidate/lib/validators";
import validationMixin from "@/mixins/validationMixin";
import UserGroupSelect from "components/UserGroupSelect";
import StateSelect from "components/StateSelect";
import DatePicker from "components/DatePicker.vue";
import UserSelect from "components/UserSelect";

const validZip = (value) =>
  !helpers.req(value) || /^\d{5}(?:[-\s]\d{4})?$/.test(value);

const defaultForm = {
  email: "",
  phoneNumber: "",
  firstName: "",
  lastName: "",
  address: "",
  address2: "",
  id: "",
  city: "",
  state: "",
  zipcode: "",
  userGroupId: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  hiredDate: "",
  firedDate: "",
  isUnion: false,
  isActive: true,
  supervisorEmail: "",
  supervisorFirstName: "",
  supervisorLastName: "",
};

export default {
  components: {
    AlertMessage,
    UserGroupSelect,
    StateSelect,
    DatePicker,
    UserSelect
  },
  props: {  
    item: Object,
    show: [Boolean, String],
  },
  mixins: [validationMixin],
  validations: {
    form: {
      userName: {
        required,
        valid: function (value) {
          return isAlphanumeric(value);
        },
      },
      email: { required, email },
      phoneNumber: {},
      firstName: { required },
      lastName: { required },
      address: {},
      address2: {},
      city: {},
      state: {},
      zipcode: {
        validZip,
      },
      userGroupId: {},
      password: {
        required: requiredIf(function () {
          return !this.isEdit;
        }),
        minLength: minLength(8),
        valid: function (value) {
          return !helpers.req(value) || isCombinationString(value);
        },
      },
      repeatPassword: {
        required: requiredIf(function () {
          return !this.isEdit;
        }),
        sameAsPassword: sameAs("password"),
      },
      emergencyContactName: {},
      emergencyContactPhone: {},
      emergencyContactRelationship: {},
      hiredDate: {},
      firedDate: {},
      isUnion: {},
      isActive: {},
      supervisorEmail: {},
      supervisorFirstName: {},
      supervisorLastName: {},
    },
  },
  validationMessages: {
    form: {
      userName: {
        valid: "Username cannot contain spaces or symbol",
        required: "Username is required.",
      },
      email: {
        required: "Please enter email",
        email: "Email must be valid",
      },
      password: {
        required: "Please enter password",
        minLength: "Password must be of 8 characters",
        valid:
          "Needs at least one uppercase, one digit and one special character",
      },
      repeatPassword: {
        required: "Required",
        sameAsPassword: "Password does not match",
      },
      firstName: {
        required: "First Name Required",
      },
      lastName: {
        required: "Last Name Required",
      },
      phoneNumber: {},
      address: {},
      address2: {},
      state: {},
      city: {},
      zipcode: {
        validZip: "Zipcode invalid",
      },
      userGroupId: {},
      emergencyContactName: {},
      emergencyContactPhone: {},
      emergencyContactRelationship: {},
      hiredDate: {},
      firedDate: {},
      isUnion: {},
      supervisorEmail: {},
      supervisorFirstName: {},
      supervisorLastName: {},
      isActive: {}
    },
  },

  data() {
    return {
      showDialog: this.show,

      form: {
        ...defaultForm,
      },

      dialog: false,
      valid: false,
      loading: false,
      showPwd: false,
    };
  },

  watch: {
    show(n, o) {
      if (n && !o) {
        this.dialog = true;
      }
    },
    dialog(n, o) {
      if (!n && o) {
        this.onDialogClose();
      } else {
        this.onDialogOpen();
      }
    },
    item(n) {
      if (n) {
        this.form = {
          ...n,
        };
      }
    },
  },

  computed: {
    ...mapState({
      account: (state) => state.account,
      alert: (state) => state.alert,
    }),
    ...mapGetters('account', ['hasAccess']),

    formTitle() {
      return this.isEdit ? "Edit User" : "Add User";
    },
    isEdit() {
      return UTIL.gv(this.item, "id", "") ? true : false;
    },
  },

  created() {},

  methods: {
    ...mapActions("account", ["create", "update"]),
    serverSideErrors(field) {
      return UTIL.gv(this.account.errors, field, []);
    },
    onDialogClose() {
      this.$emit("onClosed");
      this.$store.dispatch("alert/clear");
    },
    onDialogOpen() {
      if(this.isEdit) {
        this.form = {
          ...this.item,
        };
      }
    },
    onSupervisorSelected(userSupervisor) {
      this.form.supervisorFirstName = userSupervisor.firstName;
      this.form.supervisorLastName = userSupervisor.lastName;
    },
    save() {},
    addNew() {
      this.dialog = true;
      this.form = {
        ...defaultForm,
      };
    },

    completed() {
      this.$emit("onSucceded");
    },

    async submit() {
      this.loading = true;
      if (!this.valid) {
        this.$store.dispatch("snackbar/error", "Form invalid");
        return false;
      }

      let payload = {
        ...this.form,
        subscriptionID: UTIL.gv(this.account, "data.user.subscriptionID", ""),
      };

      if(this.hasOwn(payload, "hiredDate") && !payload.hiredDate) {
        delete payload.hiredDate;
      }

      if(this.hasOwn(payload, "firedDate") && !payload.firedDate) {
        delete payload.firedDate;
      }

      if (this.isEdit) {
        await this.update({
          ...payload,
          username: payload.userName,
        });
      } else {
        await this.create({
          ...payload,
          accountAddress: payload.address,
          accountAddress2: payload.address2,
          accountCity: payload.city,
          accountState: payload.state,
          accountZipcode: payload.zipcode,
        });
      }

      if (!this.account.status.error) {
        this.dialog = false;
        this.completed();
      }
      this.loading = false;
    },

    hasOwn(obj, key) {
      if(!obj){
        return false
      }
      return hasOwnProperty.call(obj, key);
    },
  },
};
</script>